'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { seoCache } from '@/lib/seo-cache'

interface SEOData {
  id: string
  page: string
  title: string
  description: string
  keywords: string
  noindex: boolean
}

export default function DynamicSEO() {
  const pathname = usePathname()
  const [seoData, setSeoData] = useState<SEOData | null>(null)

  useEffect(() => {
    loadSEOForCurrentPage()
  }, [pathname])

  const loadSEOForCurrentPage = async () => {
    try {
      const data = await seoCache.getSEOData()
      const currentPageSEO = data.pages?.find((page: SEOData) => page.page === pathname)
      
      if (currentPageSEO) {
        setSeoData(currentPageSEO)
        updatePageSEO(currentPageSEO)
      }
    } catch (error) {
      console.error('Failed to load SEO data:', error)
    }
  }

  const updatePageSEO = (seo: SEOData) => {
    // Update document title
    if (seo.title) {
      document.title = seo.title
    }

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    if (seo.description) {
      metaDescription.setAttribute('content', seo.description)
    }

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]')
    if (!metaKeywords && seo.keywords) {
      metaKeywords = document.createElement('meta')
      metaKeywords.setAttribute('name', 'keywords')
      document.head.appendChild(metaKeywords)
    }
    if (metaKeywords && seo.keywords) {
      metaKeywords.setAttribute('content', seo.keywords)
    }

    // Update robots meta
    let metaRobots = document.querySelector('meta[name="robots"]')
    if (!metaRobots) {
      metaRobots = document.createElement('meta')
      metaRobots.setAttribute('name', 'robots')
      document.head.appendChild(metaRobots)
    }
    const robotsContent = seo.noindex ? 'noindex, nofollow' : 'index, follow'
    metaRobots.setAttribute('content', robotsContent)

    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]')
    if (!ogTitle) {
      ogTitle = document.createElement('meta')
      ogTitle.setAttribute('property', 'og:title')
      document.head.appendChild(ogTitle)
    }
    if (seo.title) {
      ogTitle.setAttribute('content', seo.title)
    }

    let ogDescription = document.querySelector('meta[property="og:description"]')
    if (!ogDescription) {
      ogDescription = document.createElement('meta')
      ogDescription.setAttribute('property', 'og:description')
      document.head.appendChild(ogDescription)
    }
    if (seo.description) {
      ogDescription.setAttribute('content', seo.description)
    }
  }

  return null // This component doesn't render anything
} 